import moment from 'moment'
import { formatNumber, formatUsd } from '../../rewards/rewardsUtils'

// HO-TASK-289: cómo se muestra y se edita cada parámetro de RewardRule::KEYS.
//
// `unit` decide el formato:
//   - percent: se guarda como fracción (0.02) y se muestra/edita como % (2)
//   - usd, hc, xp, count, day, months: se guarda y se edita tal cual
// `range` replica RewardConfigService::RANGES en la unidad guardada; el backend
// vuelve a validar, esto solo evita mandar algo que va a rechazar.
// `zeroMeans`: texto cuando 0 tiene un significado propio (sin tope).
export const RULE_DEFINITIONS = {
  commission_rate: {
    label: 'Comisión de Homelan',
    help: 'Porcentaje que cobra Homelan sobre cada pago de cuota.',
    unit: 'percent',
    range: [0, 1]
  },
  commission_min_usd: {
    label: 'Comisión mínima',
    help: 'Lo mínimo que se cobra por transacción.',
    unit: 'usd',
    range: [0, 1000]
  },
  commission_max_usd: {
    label: 'Comisión máxima',
    help: 'Lo máximo que se cobra por transacción.',
    unit: 'usd',
    range: [0, 1000]
  },
  points_fund_commission_share: {
    label: 'Aporte al Fondo de Puntos',
    help: 'Parte de la comisión que se reserva para respaldar las Homelan Coins.',
    unit: 'percent',
    range: [0, 1],
    marginShare: true
  },
  prize_fund_collection_share: {
    label: 'Aporte al Fondo de Premios',
    help: 'Parte de la comisión que se reserva para los premios del sorteo.',
    unit: 'percent',
    range: [0, 1],
    marginShare: true
  },
  xp_per_usd: {
    label: 'XP por dólar pagado',
    help: 'Puntos de experiencia por cada USD de cuota pagada.',
    unit: 'xp',
    range: [0, 10000]
  },
  hc_per_usd: {
    label: 'Coins por dólar pagado',
    help: 'Homelan Coins por cada USD de cuota pagada, antes del multiplicador del nivel.',
    unit: 'hc',
    range: [0, 10000]
  },
  punctuality_day_limit: {
    label: 'Día límite de puntualidad',
    help: 'El pago es puntual si su fecha es anterior a este día del mes.',
    unit: 'day',
    range: [1, 28]
  },
  punctuality_bonus_xp: {
    label: 'Bono de puntualidad (XP)',
    help: 'XP extra por pagar antes del día límite.',
    unit: 'xp',
    range: [0, 100000]
  },
  punctuality_bonus_hc: {
    label: 'Bono de puntualidad (Coins)',
    help: 'Coins extra por pagar antes del día límite. No se multiplica por el nivel.',
    unit: 'hc',
    range: [0, 100000]
  },
  streak_bonus_min_months: {
    label: 'Meses para activar la racha',
    help: 'Meses seguidos pagando a tiempo y completo para ganar el bono de racha.',
    unit: 'months',
    range: [0, 60]
  },
  streak_bonus_rate: {
    label: 'Bono de racha',
    help: 'Coins adicionales sobre el multiplicador del nivel una vez alcanzada la racha.',
    unit: 'percent',
    range: [0, 5]
  },
  hc_per_usd_redemption: {
    label: 'Coins por dólar de canje',
    help: 'Homelan Coins que hay que canjear para abonar 1 USD a la cuota.',
    unit: 'hc',
    range: [0.000001, 1000000]
  },
  redemption_min_hc: {
    label: 'Canje mínimo',
    help: 'Coins mínimas por canje.',
    unit: 'hc',
    range: [0, 1000000]
  },
  redemption_max_hc: {
    label: 'Canje máximo',
    help: 'Coins máximas por canje.',
    unit: 'hc',
    range: [0, 10000000],
    zeroMeans: 'Sin tope'
  },
  referral_hc: {
    label: 'Coins por referido',
    help: 'Lo que gana quien refiere cuando el condominio referido tiene su primer pago aprobado.',
    unit: 'hc',
    range: [0, 1000000]
  },
  referral_raffle_tickets: {
    label: 'Tickets por referido',
    help: 'Tickets extra para el sorteo del mes por cada referido calificado.',
    unit: 'count',
    range: [0, 1000]
  },
  referral_monthly_cap: {
    label: 'Tope de referidos por mes',
    help: 'Referidos premiados como máximo por propietario y mes.',
    unit: 'count',
    range: [0, 1000],
    zeroMeans: 'Sin tope'
  },
  fund_coverage_threshold: {
    label: 'Cobertura mínima del fondo',
    help: 'Parte del pasivo en Coins que el Fondo de Puntos debe cubrir. 100 % es cobertura total.',
    unit: 'percent',
    range: [0, 10]
  },
  target_cost_per_transaction_usd: {
    label: 'Costo objetivo por transacción',
    help: 'Costo del programa por pago aprobado que no se debería superar.',
    unit: 'usd',
    range: [0, 1000]
  }
}

export const RULE_GROUPS = [
  {
    title: 'Comisión y fondos',
    description: 'De dónde sale el dinero del programa. Lo que no va a los fondos es el margen de Homelan.',
    keys: [
      'commission_rate',
      'commission_min_usd',
      'commission_max_usd',
      'points_fund_commission_share',
      'prize_fund_collection_share'
    ]
  },
  {
    title: 'Acumulación de puntos',
    description: 'Cuánto gana el propietario por cada pago.',
    keys: [
      'xp_per_usd',
      'hc_per_usd',
      'punctuality_day_limit',
      'punctuality_bonus_xp',
      'punctuality_bonus_hc',
      'streak_bonus_min_months',
      'streak_bonus_rate'
    ]
  },
  {
    title: 'Canje',
    description: 'Cómo se convierten las Coins en un abono a la cuota.',
    keys: ['hc_per_usd_redemption', 'redemption_min_hc', 'redemption_max_hc']
  },
  {
    title: 'Referidos',
    description: 'Premio por invitar a otro condominio.',
    keys: ['referral_hc', 'referral_raffle_tickets', 'referral_monthly_cap']
  },
  {
    title: 'Control del programa',
    description: 'Umbrales con los que el reporte marca alertas.',
    keys: ['fund_coverage_threshold', 'target_cost_per_transaction_usd']
  }
]

// Cambios que mueven el costo o el margen: antes de guardarlos se simula el
// impacto con /config/simulate.
export const MARGIN_KEYS = [
  'commission_rate',
  'commission_min_usd',
  'commission_max_usd',
  'points_fund_commission_share',
  'prize_fund_collection_share',
  'hc_per_usd',
  'hc_per_usd_redemption'
]

export const getRuleDefinition = (key) =>
  RULE_DEFINITIONS[key] || { label: key, help: '', unit: 'count', range: null }

// Nombre legible de una clave de la bitácora ('level:Oro' para los niveles).
export const getAuditKeyLabel = (key) => {
  if (key && key.startsWith('level:')) return `Nivel ${key.slice(6)}`
  return getRuleDefinition(key).label
}

// Guardado (0.02) -> editable (2), y al revés. Redondeo para no arrastrar
// 0.1 + 0.2 al formulario.
export const toInputValue = (key, value) => {
  if (value === null || value === undefined) return ''
  const { unit } = getRuleDefinition(key)
  const number = Number(value)
  return unit === 'percent' ? String(Math.round(number * 100 * 10000) / 10000) : String(number)
}

export const fromInputValue = (key, input) => {
  const number = Number(String(input).replace(',', '.'))
  if (String(input).trim() === '' || !Number.isFinite(number)) return null
  const { unit } = getRuleDefinition(key)
  return unit === 'percent' ? Math.round((number / 100) * 1000000) / 1000000 : number
}

export const formatRuleValue = (key, value) => {
  if (value === null || value === undefined) return 'Sin configurar'
  const { unit, zeroMeans } = getRuleDefinition(key)
  const number = Number(value)
  if (zeroMeans && number === 0) return zeroMeans

  switch (unit) {
    case 'percent':
      return `${formatNumber(number * 100, 2)} %`
    case 'usd':
      return formatUsd(number)
    case 'hc':
      return `${formatNumber(number, 2)} HC`
    case 'xp':
      return `${formatNumber(number, 2)} XP`
    case 'day':
      return `Día ${formatNumber(number)}`
    case 'months':
      return `${formatNumber(number)} ${number === 1 ? 'mes' : 'meses'}`
    default:
      return formatNumber(number, 2)
  }
}

export const getInputSuffix = (key) => {
  const { unit } = getRuleDefinition(key)
  return { percent: '%', usd: 'USD', hc: 'HC', xp: 'XP', months: 'meses' }[unit] || null
}

// Rango en la unidad del formulario, para el mensaje de validación.
export const getInputRange = (key) => {
  const { range } = getRuleDefinition(key)
  if (!range) return null
  return range.map((limit) => Number(toInputValue(key, limit)))
}

export const validateRuleInput = (key, input) => {
  const value = fromInputValue(key, input)
  if (value === null) return 'Ingresa un número.'
  const { range } = getRuleDefinition(key)
  if (range && (value < range[0] || value > range[1])) {
    const [min, max] = getInputRange(key)
    const suffix = getInputSuffix(key)
    return `Debe estar entre ${formatNumber(min, 6)} y ${formatNumber(max, 6)}${suffix ? ` ${suffix}` : ''}.`
  }
  return null
}

// Primera fecha en la que se puede abrir una versión nueva: el backend rechaza
// una vigencia igual o anterior a la de la versión activa.
export const getMinValidFrom = (rule, today = moment()) => {
  const base = today.clone().startOf('day')
  const activeFrom = rule?.valid_from && moment(rule.valid_from, 'YYYY-MM-DD')
  if (activeFrom && activeFrom.isValid() && !activeFrom.isBefore(base)) {
    return activeFrom.clone().add(1, 'day').format('YYYY-MM-DD')
  }
  return base.format('YYYY-MM-DD')
}

export const formatDateTime = (value) => {
  const date = value && moment(value)
  return date && date.isValid()
    ? date.toDate().toLocaleString('es-VE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : ''
}

// Proporción (1.25) como porcentaje ("125 %"); null si no hay pasivo.
export const formatRatio = (ratio) =>
  ratio === null || ratio === undefined ? 'Sin pasivo' : `${formatNumber(Number(ratio) * 100, 1)} %`

export const ACCELERATOR_TYPES = {
  service: 'Servicio',
  insurance: 'Seguro',
  referral: 'Referido',
  survey: 'Encuesta'
}

// Rangos de fecha rápidos del reporte.
export const REPORT_PRESETS = [
  { id: 'this_month', label: 'Este mes' },
  { id: 'last_month', label: 'Mes anterior' },
  { id: 'last_3_months', label: 'Últimos 3 meses' },
  { id: 'this_year', label: 'Este año' },
  { id: 'custom', label: 'Personalizado' }
]

export const getPresetRange = (presetId, today = moment()) => {
  const format = (date) => date.format('YYYY-MM-DD')
  switch (presetId) {
    case 'last_month': {
      const month = today.clone().subtract(1, 'month')
      return { startDate: format(month.clone().startOf('month')), endDate: format(month.clone().endOf('month')) }
    }
    case 'last_3_months':
      return { startDate: format(today.clone().subtract(2, 'months').startOf('month')), endDate: format(today) }
    case 'this_year':
      return { startDate: format(today.clone().startOf('year')), endDate: format(today) }
    default:
      return { startDate: format(today.clone().startOf('month')), endDate: format(today) }
  }
}
